import React, { useMemo, useState } from "react";
import { Button, useTheme } from "@mui/material";
import { styled } from "@iventis/styles";
import { Permission } from "@iventis/domain-model/model/permission";
import { Theme } from "@emotion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { inlineTextIconMargin, sectionalMargin } from "@iventis/styles/src/atomic-rules";
import { Content } from "@iventis/translations/content/typed-content";
import { PermissionResourceType } from "@iventis/domain-model/model/permissionResourceType";
import { PermissionOwnerType } from "@iventis/domain-model/model/permissionOwnerType";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { Body1, Header5 } from "@iventis/styles/src/components/texts";
import { LoadingComponent } from "@iventis/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { compareAuthorisationsDesc, isLocked } from "@iventis/utilities";
import IconButtonComponent from "@iventis/components/src/icon-button";
import CustomSwitchComponent from "@iventis/components/src/switch-component";
import HintLabel from "@iventis/components/src/hint-label";
import { PermissionEntry } from "./permission-entry";
import { UserSearchModalComponent } from "./user-search-modal";
import { getLabel, getPermissionResourceIconName } from "./permission-utilitites";
import { GroupOption, UserOption } from "./user-search.types";
import { BreakInhertianceModal } from "./break-inheritance-modal";
import { RestoreInhertianceModal } from "./restore-inheritance-modal";

const enum PermissionHelperModal {
    UserSearch,
    BreakInheritance,
    RestoreInheritance,
}

export const PermissionsResourceComponent: React.FC<{
    permissions: Permission[];
    title: string;
    className?: string;
    resourceId: string;
    resourceType: PermissionResourceType;
    openPermissionSelectorId?: string;
    disabled?: boolean;
    loading?: boolean;
    addPermission: (permission: Permission) => void;
    removePermission: (permission: Permission) => void;
    changePermission: (permission: Permission, authorisation: Authorisation) => void;
    changeInheritance: (resourceId: string, inheritance: boolean) => void;
    canLock: boolean; // Can the user lock / unlock the resource.
}> = ({
    permissions,
    title,
    className,
    resourceId,
    resourceType,
    addPermission,
    removePermission,
    changePermission,
    openPermissionSelectorId,
    disabled,
    loading,
    changeInheritance,
    canLock,
}) => {
    const theme = useTheme<Theme>();
    const [permissionHelperModalOpen, setPermissionHelperModalOpen] = useState<PermissionHelperModal>(null);
    const translate = useIventisTranslate();
    const inheritanceEnabled =
        permissions.filter((p) => p.resourceId === resourceId && (p.authorisation === Authorisation.Break || p.authorisation === Authorisation.PermanentBreak)).length === 0;
    const allowLocks = useMemo(() => resourceType === PermissionResourceType.ScheduleVersion, [resourceType]);
    const lockedState = useMemo(
        () => ({
            locked: isLocked(permissions), // Is the resource in a locked state (this could be a locked permission from a parent or on the resource itself)
            resourceLockPermission: permissions.find((p) => p.authorisation === Authorisation.Lock && p.resourceId === resourceId), // Does the resource itself have a locked permission on it. One lock permission per resource.
            inheritedLockPermissions: permissions.filter((p) => p.authorisation === Authorisation.Lock && p.resourceId !== resourceId), // Does the resource inherit any lock permissions. There could be muiltiple locks being inherited from multiples resources.
        }),
        [permissions]
    );

    const disablePermissions = useMemo(() => disabled || lockedState.locked, [lockedState, disabled]);
    const dividerCommonStyles = { height: sectionalMargin, backgroundColor: theme.secondaryColors.light20 };

    const addNewPermission = async (item: GroupOption | UserOption) => {
        setPermissionHelperModalOpen(null);
        addPermission({
            id: undefined,
            ownerType: item.type as PermissionOwnerType,
            ownerId: item.value.id,
            ownerName: getLabel(item),
            resourceType,
            resourceId,
            // Authorisation: undefined signals that we need to select one
            authorisation: undefined,
            inheritable: true,
        });
    };

    const addLockPermission = async () => {
        addPermission({
            id: undefined,
            ownerType: PermissionOwnerType.Resource,
            ownerId: resourceId,
            ownerName: "Resource Lock",
            resourceType,
            resourceId,
            authorisation: Authorisation.Lock,
            inheritable: true,
        });
    };

    return (
        <section style={{ position: "relative" }} className={className}>
            {loading && (
                <div style={{ position: "absolute", backgroundColor: "rgba(0, 0, 0, 0.2)", height: "100%", width: "100%", zIndex: 5000 }}>
                    <LoadingComponent />
                </div>
            )}
            {/* Locked Header */}
            {allowLocks && lockedState.locked && (
                <LockHeader theme={theme}>
                    <Body1>
                        {lockedState.resourceLockPermission ? (
                            <span>{translate(Content.permissions.resourceLockDescription1)}</span>
                        ) : (
                            <span>{translate(Content.permissions.inheritedLockDescription1)}</span>
                        )}
                        <br />
                        {lockedState.resourceLockPermission ? (
                            <span>{translate(Content.permissions.resourceLockDescription2)}</span>
                        ) : (
                            <span>{translate(Content.permissions.inheritedLockDescription2)}</span>
                        )}
                    </Body1>
                    <Button disabled={!lockedState.resourceLockPermission || !canLock} onClick={() => removePermission(lockedState.resourceLockPermission)} variant="outlined">
                        {lockedState.resourceLockPermission && <span>{translate(Content.permissions.unlockButtonText)}</span>}
                        <FontAwesomeIcon icon={["fas", "lock"]} style={{ paddingLeft: "4px" }} />
                    </Button>
                </LockHeader>
            )}
            {/* Resource title section */}
            <ResourceHeader theme={theme}>
                <Header5>
                    <FontAwesomeIcon icon={getPermissionResourceIconName(resourceType)} style={{ marginRight: inlineTextIconMargin }} />
                    {title}
                </Header5>
                <div style={{ display: "flex", alignItems: "center", marginLeft: "10px", gap: "5px" }}>
                    {/* Enable/Disable Inherited Permissions */}
                    <CustomSwitchComponent
                        testId="permission-inheritance-switch"
                        label={
                            <HintLabel
                                label={translate(Content.permissions.useInheritedPermissions, { resource: translate(Content.library.node_names.Folder) })}
                                hintText={translate(Content.permissions.inheritedPermissionsHelpText)}
                            />
                        }
                        disabled={disablePermissions}
                        onChange={(event) =>
                            event.target.checked
                                ? setPermissionHelperModalOpen(PermissionHelperModal.RestoreInheritance)
                                : setPermissionHelperModalOpen(PermissionHelperModal.BreakInheritance)
                        }
                        checked={inheritanceEnabled}
                    />
                    {/* Unlock button Icon */}
                    {allowLocks && !lockedState.locked && (
                        <IconButtonComponent
                            height={30}
                            width={30}
                            disabled={!canLock}
                            onClick={() => {
                                addLockPermission();
                            }}
                            hiddenText={translate(Content.permissions.lock)}
                        >
                            <FontAwesomeIcon icon={["fas", "lock-open"]} style={{ color: "white", height: "1rem", width: "1rem" }} />
                        </IconButtonComponent>
                    )}
                </div>
            </ResourceHeader>

            {/* Inherited Permissions */}
            <div style={{ display: "flex", flexDirection: "column", backgroundColor: theme.secondaryColors.light20, padding: "20px" }}>
                <Header5 style={inheritanceEnabled ? {} : { textDecoration: "line-through" }}>{translate(Content.permissions.inheritedPermissions)}</Header5>
                {!inheritanceEnabled && <Body1 style={{ paddingTop: "20px" }}>Disabled</Body1>}
            </div>
            <div>
                {permissions
                    .filter(
                        (permission) =>
                            permission.resourceId !== resourceId &&
                            permission.authorisation !== Authorisation.Break &&
                            permission.authorisation !== Authorisation.PermanentBreak &&
                            permission.authorisation !== Authorisation.Lock
                    )
                    .sort((a, b) => compareAuthorisationsDesc(a.authorisation, b.authorisation))
                    .map((permission) => (
                        <PermissionEntry disabled permission={permission} key={permission.id} removeable={false} change={changePermission} resourceType={resourceType} />
                    ))}
            </div>

            {/* Divider between permissions */}
            <div style={{ display: "flex", justifyContent: "stretch", alignItems: "bottom" }}>
                <div style={{ ...dividerCommonStyles, flex: "0 0 20px" }} />
                <div style={{ ...dividerCommonStyles, height: "19px", flex: "1", borderBottom: `1px solid ${theme.shades.three}` }} />
                <div style={{ ...dividerCommonStyles, flex: "0 0 20px" }} />
            </div>

            {/* Additional Permissions */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: theme.secondaryColors.light20, padding: "20px" }}>
                <Header5>{translate(Content.permissions.additionalPermissions)}</Header5>
                <Button
                    color="primary"
                    disabled={disablePermissions}
                    onClick={() => setPermissionHelperModalOpen(PermissionHelperModal.UserSearch)}
                    data-testid="add-user-or-group"
                >
                    <FontAwesomeIcon icon="circle-plus" style={{ marginRight: inlineTextIconMargin }} />
                    {translate(Content.permissions.addUserOrGroup)}
                </Button>
            </div>
            <div>
                {permissions
                    .filter(
                        (permission) =>
                            permission.resourceId === resourceId &&
                            permission.authorisation !== Authorisation.Break &&
                            permission.authorisation !== Authorisation.PermanentBreak &&
                            permission.authorisation !== Authorisation.Lock
                    )
                    .sort((a, b) => compareAuthorisationsDesc(a.authorisation, b.authorisation))
                    .map((permission) => (
                        <PermissionEntry
                            disabled={disablePermissions}
                            removeable
                            selectorOpen={openPermissionSelectorId === permission.id ? true : undefined}
                            permission={permission}
                            key={permission.id}
                            remove={removePermission}
                            change={changePermission}
                            resourceType={resourceType}
                        />
                    ))}
            </div>

            <UserSearchModalComponent
                open={permissionHelperModalOpen === PermissionHelperModal.UserSearch}
                close={() => setPermissionHelperModalOpen(null)}
                onSelected={addNewPermission}
            />
            <BreakInhertianceModal
                open={permissionHelperModalOpen === PermissionHelperModal.BreakInheritance}
                onClose={() => setPermissionHelperModalOpen(null)}
                onConfirm={() => {
                    setPermissionHelperModalOpen(null);
                    changeInheritance(resourceId, false);
                }}
            />
            <RestoreInhertianceModal
                open={permissionHelperModalOpen === PermissionHelperModal.RestoreInheritance}
                onClose={() => setPermissionHelperModalOpen(null)}
                onConfirm={() => {
                    setPermissionHelperModalOpen(null);
                    changeInheritance(resourceId, true);
                }}
            />
        </section>
    );
};

const ResourceHeader = styled("header")`
    background-color: ${(props) => props.theme.secondaryColors.warm};
    color: ${(props) => props.theme.typographyColors.core};
    font-weight: 600;
    padding: 15px 20px 15px 20px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
`;

const LockHeader = styled("header")`
    background-color: ${(props) => props.theme.secondaryColors.warm};
    color: ${(props) => props.theme.typographyColors.core};
    font-weight: 600;
    padding: 15px 20px 15px 20px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
`;
